import React from "react";
import { motion } from "framer-motion";

export default function AboutMe() {
  const experiences = [
    {
      id: 1,
      role: "Technical Team Lead",
      company: "Ecom Experts",
      duration: "2023 - Present",
      description: "Leading a team of developers in delivering complex Shopify solutions for enterprise clients. Overseeing project architecture, code reviews, and mentoring junior developers. Responsible for client relationships and ensuring high-quality deliverables on time and within budget.",
      achievements: ["Led 25+ enterprise Shopify projects", "Managed team of 5 developers", "Increased team productivity by 40%", "Established development standards and best practices"]
    },
    {
      id: 2,
      role: "Shopify Developer",
      company: "Cronix LLC",
      duration: "2024 - 2025",
      description: "Developing custom Shopify solutions for diverse clients ranging from startups to established brands. Specializing in theme customization, checkout extensibility, and third-party integrations. Focus on performance optimization and conversion rate improvements.",
      achievements: ["Built 20+ Shopify stores", "Increased average page speed by 45%", "Achieved 98% client satisfaction rate"]
    },
    {
      id: 3,
      role: "Shopify Developer",
      company: "Ecom Experts",
      duration: "2023 - 2024",
      description: "Specialized in Shopify theme development and store customization. Created responsive, mobile-first e-commerce experiences with advanced Liquid templating and JavaScript functionality. Collaborated with designers and project managers to deliver pixel-perfect implementations.",
      achievements: ["Delivered 35+ Shopify projects", "Improved average conversion rates by 28%", "Mastered advanced Liquid templating", "Reduced development time by 30%"]
    },
    {
      id: 4,
      role: "CMS Developer",
      company: "Software Pro",
      duration: "2021 - 2023",
      description: "Developed and maintained content management systems with focus on e-commerce platforms. Worked extensively with WordPress, Shopify, and custom CMS solutions. Responsible for API integrations, and performance optimization.",
      achievements: ["Built 20+ CMS-based websites", "Improved site loading speed by 35%", "Trained 3 junior developers"]
    },
    {
      id: 5,
      role: "Frontend Developer",
      company: "Software Pro",
      duration: "2020 - 2021",
      description: "Started career focusing on frontend development using modern JavaScript frameworks and responsive design. Built user interfaces for web applications and e-commerce sites. Gained foundational experience in HTML, CSS, JavaScript, and began exploring Shopify development.",
      achievements: ["Completed 30+ frontend projects", "Achieved Shopify Partner certification"]
    }
  ];

  const achievements = [
    { icon: "🛒", title: "50+ Stores", description: "Shopify stores delivered" },
    { icon: "💰", title: "$2M+ Revenue", description: "Generated for clients" },
    { icon: "⚡", title: "40% Faster", description: "Average speed improvement" },
    { icon: "📈", title: "35% Higher", description: "Average conversion increase" },
    { icon: "🏆", title: "Shopify Expert", description: "Certified Partner status" },
    { icon: "🌍", title: "Global Clients", description: "From 15+ countries" }
  ];

  const containerVariants = {
    initial: {},
    animate: {
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    initial: { opacity: 0, y: 30 },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const achievementVariants = {
    initial: { opacity: 0, scale: 0.8 },
    animate: {
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20
      }
    }
  };

  return (
    <motion.div 
      className="max-w-4xl mx-auto"
      variants={containerVariants}
      initial="initial"
      animate="animate"
    >
      <motion.div 
        className="text-center mb-8 sm:mb-12"
        variants={itemVariants}
      >
        <motion.h2 
          className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3 sm:mb-4"
          whileHover={{ scale: 1.02 }}
        >
          About Me
        </motion.h2>
        <motion.p 
          className="text-sm sm:text-base md:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed"
          whileHover={{ color: "#374151" }}
          transition={{ duration: 0.3 }}
        >
          I'm a passionate full-stack developer with over 5 years of specialized experience in 
          e-commerce and CMS development. I help businesses scale by creating high-converting, 
          performance-optimized solutions across multiple platforms including Shopify, WordPress, and modern CMS.
        </motion.p>
      </motion.div>

      {/* Personal Story */}
      <motion.div 
                  className="mb-16 p-8 bg-gradient-soft rounded-2xl shadow-lg"
        variants={itemVariants}
        whileHover={{ 
          scale: 1.01,
          transition: { duration: 0.3 }
        }}
      >
        <motion.h3 
          className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 mb-3 sm:mb-4"
          whileHover={{ x: 5 }}
          transition={{ duration: 0.2 }}
        >
          My Development Journey
        </motion.h3>
        <motion.p 
          className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4"
          initial={{ opacity: 0.8 }}
          whileHover={{ opacity: 1 }}
        >
          My journey into web development began with frontend technologies, then I discovered the power of various CMS platforms to 
          transform businesses. What started as curiosity about online stores quickly evolved into 
          a deep passion for creating exceptional e-commerce experiences that drive real business results.
        </motion.p>
        <motion.p 
          className="text-sm sm:text-base text-gray-700 leading-relaxed"
          initial={{ opacity: 0.8 }}
          whileHover={{ opacity: 1 }}
        >
          Today, I specialize in multi-platform development, helping businesses of all sizes scale 
          their online presence. From custom app development to performance optimization, I focus on 
          creating solutions that not only look great but also convert visitors into loyal customers.
        </motion.p>
      </motion.div>

      {/* Experience Timeline */}
      <motion.div 
        className="mb-16"
        variants={itemVariants}
      >
        <motion.h3 
          className="text-xl sm:text-2xl font-semibold text-gray-900 mb-8 text-center"
          whileHover={{ scale: 1.02 }}
        >
          Professional Experience
        </motion.h3>
        
        <div className="space-y-8">
          {experiences.map((exp, index) => (
            <motion.div
              key={exp.id}
              className="relative pl-8 border-l-2 border-gray-200"
              variants={itemVariants}
              whileHover={{ x: 5 }}
              transition={{ duration: 0.2 }}
            >
              <motion.div 
                className="absolute -left-2 top-0 w-4 h-4 bg-purple-500 rounded-full"
                whileHover={{ scale: 1.5 }}
                transition={{ duration: 0.2 }}
              />
              
              <motion.div 
                className="bg-white p-6 rounded-xl shadow-sm border border-gray-100"
                whileHover={{ 
                  y: -3,
                  boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
                  transition: { duration: 0.2 }
                }}
              >
                <div className="flex flex-wrap items-center justify-between mb-2">
                  <motion.h4 
                    className="text-lg font-semibold text-gray-900"
                    whileHover={{ color: "#8b5cf6" }}
                  >
                    {exp.role}
                  </motion.h4>
                  <motion.span 
                    className="text-sm font-medium text-purple-600 bg-purple-50 px-3 py-1 rounded-full"
                    whileHover={{ scale: 1.05 }}
                  >
                    {exp.duration}
                  </motion.span>
                </div>
                
                <motion.p 
                  className="text-gray-600 font-medium mb-3"
                  whileHover={{ color: "#374151" }}
                >
                  {exp.company}
                </motion.p>
                
                <motion.p 
                  className="text-gray-700 mb-4 leading-relaxed"
                  initial={{ opacity: 0.8 }}
                  whileHover={{ opacity: 1 }}
                >
                  {exp.description}
                </motion.p>
                
                <div className="flex flex-wrap gap-2">
                  {exp.achievements.map((achievement, achIndex) => (
                    <motion.span
                      key={achIndex}
                      className="text-xs bg-gray-100 text-gray-700 px-3 py-1 rounded-full"
                      whileHover={{ 
                        backgroundColor: "#8b5cf6",
                        color: "white",
                        scale: 1.05
                      }}
                      transition={{ duration: 0.2 }}
                    >
                      {achievement}
                    </motion.span>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Achievements Grid */}
      <motion.div 
        className="mb-16"
        variants={itemVariants}
      >
        <motion.h3 
          className="text-xl sm:text-2xl font-semibold text-gray-900 mb-8 text-center"
          whileHover={{ scale: 1.02 }}
        >
          Key Achievements
        </motion.h3>
        
        <motion.div 
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6"
          variants={containerVariants}
        >
          {achievements.map((achievement, index) => (
            <motion.div
              key={index}
              className="text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100"
              variants={achievementVariants}
              whileHover={{ 
                y: -5,
                scale: 1.05,
                boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
                transition: { duration: 0.2 }
              }}
            >
              <motion.div 
                className="text-3xl mb-3"
                whileHover={{ 
                  scale: 1.3,
                  rotate: 10,
                  transition: { duration: 0.3 }
                }}
              >
                {achievement.icon}
              </motion.div>
              <motion.h4 
                className="font-semibold text-gray-900 mb-1"
                whileHover={{ color: "#8b5cf6" }}
              >
                {achievement.title}
              </motion.h4>
              <motion.p 
                className="text-sm text-gray-600"
                initial={{ opacity: 0.8 }}
                whileHover={{ opacity: 1 }}
              >
                {achievement.description}
              </motion.p>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>

      {/* Personal Interests */}
      <motion.div 
                  className="p-8 bg-gradient-dark text-white rounded-2xl"
        variants={itemVariants}
      >
        <motion.h3 
          className="text-xl sm:text-2xl font-semibold mb-4"
          whileHover={{ scale: 1.02 }}
        >
          Beyond E-commerce
        </motion.h3>
        <motion.p 
          className="text-gray-300 leading-relaxed mb-6"
          initial={{ opacity: 0.8 }}
          whileHover={{ opacity: 1 }}
        >
          When I'm not building Shopify stores, I enjoy staying updated with the latest e-commerce trends, 
          contributing to the Shopify community, and sharing knowledge through tutorials and blog posts. 
          I'm also passionate about UI/UX design, conversion optimization, and helping small businesses 
          grow their online presence.
        </motion.p>
        
        <div className="flex flex-wrap gap-3">
          {["E-commerce Trends", "UI/UX Design", "Conversion Optimization", "Shopify Community", "Business Growth"].map((interest, index) => (
            <motion.span
              key={interest}
              className="px-4 py-2 bg-gray-800 rounded-full text-sm text-gray-300 cursor-pointer"
              initial={{ opacity: 0.9 }}
              animate={{ opacity: 1 }}
              whileHover={{ 
                backgroundColor: "#8b5cf6",
                color: "white",
                scale: 1.1,
                boxShadow: "0 4px 12px rgba(139, 92, 246, 0.3)"
              }}
              whileTap={{ scale: 0.95 }}
              transition={{ 
                type: "tween",
                duration: 0.2,
                ease: "easeInOut"
              }}
            >
              {interest}
            </motion.span>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
} 